import React, { useEffect, useRef, useState } from 'react';
import { Pause, Play, RotateCcw } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';

interface VideoPlayerModalProps {
  title: string;
  description?: string | null;
  videoUrl?: string | null;
  onClose: () => void;
  onEnded?: () => void;
  lockSeeking?: boolean;
  lockSeekingMessage?: string;
  unavailableMessage?: string;
}

export const VideoPlayerModal: React.FC<VideoPlayerModalProps> = ({
  title,
  description,
  videoUrl,
  onClose,
  onEnded,
  lockSeeking = false,
  lockSeekingMessage = 'Fast-forward and skip are disabled for this required video.',
  unavailableMessage = 'Video not available',
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const maxWatchedTimeRef = useRef(0);
  const hasAttemptedFullscreenRef = useRef(false);

  const requestFullscreen = async () => {
    const video = videoRef.current;
    if (!video || document.fullscreenElement || hasAttemptedFullscreenRef.current) return;

    hasAttemptedFullscreenRef.current = true;

    const fullscreenVideo = video as HTMLVideoElement & {
      webkitRequestFullscreen?: () => Promise<void> | void;
      webkitEnterFullscreen?: () => void;
      msRequestFullscreen?: () => Promise<void> | void;
    };

    try {
      if (typeof fullscreenVideo.requestFullscreen === 'function') {
        await fullscreenVideo.requestFullscreen();
      } else if (typeof fullscreenVideo.webkitRequestFullscreen === 'function') {
        await fullscreenVideo.webkitRequestFullscreen();
      } else if (typeof fullscreenVideo.msRequestFullscreen === 'function') {
        await fullscreenVideo.msRequestFullscreen();
      } else if (typeof fullscreenVideo.webkitEnterFullscreen === 'function') {
        fullscreenVideo.webkitEnterFullscreen();
      }
    } catch {
      // Browser fullscreen calls can be blocked without a user gesture.
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onLoadedMetadata = () => {
      setPlaybackError(null);
      maxWatchedTimeRef.current = 0;
      hasAttemptedFullscreenRef.current = false;
    };
    const onCanPlay = () => {
      setIsPlaying(!video.paused);
      void requestFullscreen();
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTimeUpdate = () => {
      if (!lockSeeking) return;
      if (video.currentTime > maxWatchedTimeRef.current) {
        maxWatchedTimeRef.current = video.currentTime;
      }
    };
    const onSeeking = () => {
      if (!lockSeeking) return;
      const maxAllowed = maxWatchedTimeRef.current + 1;
      if (video.currentTime > maxAllowed) {
        video.currentTime = maxWatchedTimeRef.current;
      }
    };
    const onError = () => {
      setPlaybackError(
        'This video could not be decoded by your browser. Please re-encode to H.264 (AVC) + AAC in MP4.',
      );
    };

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('seeking', onSeeking);
    video.addEventListener('error', onError);
    return () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('seeking', onSeeking);
      video.removeEventListener('error', onError);
    };
  }, [videoUrl, lockSeeking]);

  const handleTogglePlayback = async () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      await video.play();
    } else {
      video.pause();
    }
  };

  const handleRestart = async () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    await video.play();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : (
            <DialogDescription aria-hidden="true" className="sr-only">
              Video player
            </DialogDescription>
          )}
        </DialogHeader>
        {videoUrl ? (
          <>
            <video
              ref={videoRef}
              src={videoUrl}
              controls={!lockSeeking}
              className="w-full rounded-md"
              autoPlay
              onEnded={onEnded}
            />
            {lockSeeking && (
              <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                {lockSeekingMessage}
              </div>
            )}
            {lockSeeking && (
              <div className="mt-3 flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={handleTogglePlayback}>
                  {isPlaying ? (
                    <>
                      <Pause className="mr-1 h-4 w-4" />
                      Pause
                    </>
                  ) : (
                    <>
                      <Play className="mr-1 h-4 w-4" />
                      Play
                    </>
                  )}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={handleRestart}>
                  <RotateCcw className="mr-1 h-4 w-4" />
                  Restart
                </Button>
              </div>
            )}
            {playbackError && (
              <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {playbackError}
              </div>
            )}
          </>
        ) : (
          <div className="flex h-48 items-center justify-center rounded-md bg-muted">
            <p className="text-sm text-muted-foreground">{unavailableMessage}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
